import { inject, observer } from 'mobx-react';
import { AnalyticsTrigger, ArrowList, ArrowListItem, Heading, Separator } from 'telia-front-react';
import React from 'react';
import { v4 } from 'uuid';
import Config from '../../Config';
import LanguageStore from '../../stores/LanguageStore';
import RouteStore from '../../stores/RouteStore';
import SearchStore, { ONLINE_HELP_CATEGORY } from '../../stores/SearchStore';
import SpinnerWithSpacing from '../../components/SpinnerWithSpacing';
import SearchResultsContainer from './SearchResultsContainer';
import { OnlineHelpSearchResult, SearchEvent } from '../SearchInterfaces';
import SearchResultRow from '../row/SearchResultRow';
import ShowMoreButton from '../ShowMoreButton';

interface OnlineHelpSearchResultsProps {
  searchStore?: SearchStore;
  routeStore?: RouteStore;
  languageStore?: LanguageStore;
}

@observer
class OnlineHelpSearchResults extends React.Component<OnlineHelpSearchResultsProps> {
  public render() {
    const {
      searchStore: { searchWrappers },
      routeStore
    } = this.props;

    if (['UNINITIALIZED', 'ERROR'].includes(searchWrappers[ONLINE_HELP_CATEGORY].status)) {
      return null;
    }

    if (searchWrappers[ONLINE_HELP_CATEGORY].status === 'FETCHING') {
      return <SpinnerWithSpacing />;
    }

    return (
      <SearchResultsContainer>
        <Separator spacing="xsmall" weight="thin" />
        <ArrowList>
          <ArrowList large={true} backgroundColor="white">
            {searchWrappers[ONLINE_HELP_CATEGORY].results.map((result: OnlineHelpSearchResult, index) => (
              <SearchResultRow key={result.id || `online-help-search-result-${index}`}>
                <AnalyticsTrigger trigger={{ id: `Tab - ${ONLINE_HELP_CATEGORY} - Link - ${result.title}`, callback: 'onClick' }}>
                  <ArrowListItem
                    url={result.link}
                    onClick={() => {
                      routeStore.hideSpinner();
                      this.postSearchResultClick(result);
                    }}
                    key={result.id}
                  >
                    <Heading element="h3" className="h4">
                      {result.title}
                    </Heading>
                    {result.highlight && (
                      <>
                        <Separator spacing="xxsmall" color="invisible" />
                        <p className="text-weight-normal">{result.highlight}</p>
                      </>
                    )}
                  </ArrowListItem>
                  <Separator spacing="xxsmall" weight="thin" fullWidth={true} />
                </AnalyticsTrigger>
              </SearchResultRow>
            ))}
          </ArrowList>
        </ArrowList>
        {searchWrappers[ONLINE_HELP_CATEGORY].status !== 'REFETCHING' && <ShowMoreButton category={ONLINE_HELP_CATEGORY} />}
      </SearchResultsContainer>
    );
  }

  private postSearchResultClick = (result: OnlineHelpSearchResult) => {
    const { searchStore, languageStore } = this.props;
    const idxName = `${Config.componentTier}_oa${languageStore.currentLanguage === 'et' ? '' : `-${languageStore.currentLanguage}`}`;
    const searchEvent: SearchEvent = {
      eventName: 'Online search result click',
      eventType: 'click',
      index: idxName,
      objectIDs: [`${result.id}`],
      userToken: v4(),
      queryID: searchStore.searchWrappers[ONLINE_HELP_CATEGORY].queryId,
      positions: [searchStore.searchWrappers[ONLINE_HELP_CATEGORY].results.indexOf(result) + 1]
    };

    searchStore.postSearchTabAnalytics(ONLINE_HELP_CATEGORY);
    searchStore.postSearchResultClickEvent(searchEvent);
  };
}

export default inject('searchStore', 'routeStore', 'languageStore')(OnlineHelpSearchResults);
